import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Button, Spinner, Container, Badge,
    Modal, ModalHeader, ModalBody, Row, Col, InputGroup, Input, Table
} from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost } from "../../Utils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { Activity, CheckCircle, AlertTriangle, RefreshCw, Eye } from 'react-feather';
import { useAuth } from "../../Context/AuthContext";
import { H3 } from "../../AbstractElements";

interface AuditLogItem {
    id: number;
    user_id: number | null;
    log_name: string;
    action: string;
    description: string;
    subject_type: string | null;
    subject_id: number | null;
    old_values: Record<string, any> | null;
    new_values: Record<string, any> | null;
    ip_address: string;
    user_agent: string;
    created_at: string;
    user?: {
        id: number;
        name: string;
        email: string;
    } | null;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function AuditLogs() {
    const [logs, setLogs] = useState<AuditLogItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(15);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedModule, setSelectedModule] = useState("");
    const [selectedAction, setSelectedAction] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [sortBy, setSortBy] = useState("id");
    const [sortDirection, setSortDirection] = useState("desc");
    const [refresh, setRefresh] = useState(0);

    const [viewModal, setViewModal] = useState(false);
    const [selectedLog, setSelectedLog] = useState<AuditLogItem | null>(null);
    const [permissionsList, setPermissionsList] = useState<any[]>([]);

    const { hasPermission } = useAuth();

    useEffect(() => {
        fetchPermissionsMaster();
    }, []);

    const fetchPermissionsMaster = async () => {
        try {
            const res: any = await apiPost('role/get_permissions');
            if (Array.isArray(res)) {
                setPermissionsList(res);
            }
        } catch (e) {
            console.error("Failed to load permissions master", e);
        }
    };

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            fetchData(currentPage, perPage, searchTerm, selectedModule, selectedAction, startDate, endDate, sortBy, sortDirection);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, selectedModule, selectedAction, startDate, endDate, refresh, currentPage, perPage, sortBy, sortDirection]);

    const fetchData = async (
        page: number, size: number, search: string, moduleName: string,
        actionName: string, start: string, end: string, sortCol: string = "id", sortDir: string = "desc"
    ) => {
        setLoading(true);
        try {
            const response: any = await apiPost(`audit-logs/list?page=${page}`, {
                per_page: size,
                search,
                log_name: moduleName,
                action: actionName,
                start_date: start,
                end_date: end,
                sort_by: sortCol,
                sort_direction: sortDir,
            });

            if (response && response.logs) {
                setLogs(response.logs.data || []);
                setTotalRows(response.logs.total || 0);
            }
        } catch (error) {
            console.error("Error fetching audit logs:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: any, sortDirectionStr: string) => {
        if (column.sortField) {
            setSortBy(column.sortField);
            setSortDirection(sortDirectionStr);
        }
    };

    const handleViewDetails = (log: AuditLogItem) => {
        setSelectedLog(log);
        setViewModal(true);
    };

    const renderActionBadge = (action: string) => {
        switch (action.toUpperCase()) {
            case 'CREATE':
                return <Badge color="success">CREATE</Badge>;
            case 'UPDATE':
                return <Badge color="primary">UPDATE</Badge>;
            case 'DELETE':
                return <Badge color="danger">DELETE</Badge>;
            case 'LOGIN':
            case 'LOGOUT':
                return <Badge color="info">{action}</Badge>;
            default:
                return <Badge color="secondary">{action}</Badge>;
        }
    };

    const renderDiffTable = (oldValues: Record<string, any> | null, newValues: Record<string, any> | null) => {
        if (!oldValues && !newValues) {
            return <div className="text-muted p-3 text-center">No field-level diff data recorded for this action.</div>;
        }

        const allKeys = Array.from(new Set([
            ...(oldValues ? Object.keys(oldValues) : []),
            ...(newValues ? Object.keys(newValues) : [])
        ]));

        // Filter keys to show ONLY changed fields when both old and new values are present (e.g. UPDATE)
        const keys = allKeys.filter((key) => {
            if (oldValues && newValues) {
                return JSON.stringify(oldValues[key]) !== JSON.stringify(newValues[key]);
            }
            return true;
        });

        if (keys.length === 0) {
            return <div className="text-muted p-3 text-center">No field-level changes detected for this action.</div>;
        }

        return (
            <Table responsive striped bordered hover className="mt-2">
                <thead className="table-light">
                    <tr>
                        <th style={{ width: '25%' }}>Field Name</th>
                        <th style={{ width: '37.5%' }}>Old Value</th>
                        <th style={{ width: '37.5%' }}>New Value</th>
                    </tr>
                </thead>
                <tbody>
                    {keys.map((key) => {
                        const oldVal = oldValues ? oldValues[key] : null;
                        const newVal = newValues ? newValues[key] : null;

                        const formatVal = (val: any, fieldKey: string) => {
                            if (val === null || val === undefined) return <span className="text-muted italic">empty</span>;
                            if (typeof val === 'boolean') return val ? 'true' : 'false';

                            // Format permissions list as clean Module-grouped Matrix table matching Role Details view
                            if (fieldKey === 'permissions' && Array.isArray(val)) {
                                if (val.length === 0) return <span className="text-muted italic">none</span>;

                                const grouped: Record<string, string[]> = {};

                                val.forEach((item: any) => {
                                    const match = permissionsList.find((p) => p.id === Number(item) || p.slug === String(item));
                                    if (match) {
                                        if (!grouped[match.module]) grouped[match.module] = [];
                                        if (!grouped[match.module].includes(match.action)) {
                                            grouped[match.module].push(match.action);
                                        }
                                    } else {
                                        const str = String(item);
                                        const parts = str.split('.');
                                        const mod = parts[0] ? parts[0].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'CUSTOM';
                                        const act = parts[1] || parts[0];
                                        if (!grouped[mod]) grouped[mod] = [];
                                        if (!grouped[mod].includes(act)) grouped[mod].push(act);
                                    }
                                });

                                return (
                                    <div
                                        className="rounded border shadow-xs"
                                        style={{
                                            maxHeight: '220px',
                                            overflowY: 'auto',
                                            overflowX: 'hidden',
                                            backgroundColor: '#ffffff'
                                        }}
                                    >
                                        <Table size="sm" className="mb-0 align-middle">
                                            <thead className="table-light sticky-top" style={{ fontSize: '11px', zIndex: 1 }}>
                                                <tr>
                                                    <th style={{ width: '38%' }}>Module</th>
                                                    <th>Permissions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.keys(grouped).sort().map((moduleName) => (
                                                    <tr key={moduleName}>
                                                        <td className="fw-bold text-dark ps-2 align-top py-2" style={{ fontSize: '12px' }}>{moduleName}</td>
                                                        <td className="py-2">
                                                            <div className="d-flex flex-wrap align-items-center gap-1">
                                                                {grouped[moduleName].map((act: string) => (
                                                                    <Badge key={act} color="success" pill className="px-2 py-1 d-inline-flex align-items-center" style={{ fontSize: '10px', fontWeight: '600' }}>
                                                                        <i className="fa fa-check-circle me-1" style={{ fontSize: '9px' }}></i> {act}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                );
                            }

                            if (typeof val === 'object') {
                                return (
                                    <pre className="mb-0 p-2 bg-light border rounded small" style={{ maxHeight: '150px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {JSON.stringify(val, null, 2)}
                                    </pre>
                                );
                            }

                            const strVal = String(val);

                            // Format ISO dates or date strings (e.g. 1995-06-26T18:30:00.000000Z or dob/date fields)
                            if (
                                (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/.test(val)) ||
                                ['dob', 'joining_date', 'date'].includes(fieldKey)
                            ) {
                                const parsedDate = new Date(strVal);
                                if (!isNaN(parsedDate.getTime())) {
                                    const formattedDate = parsedDate.toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                    });
                                    if (strVal.includes('T') && !['dob', 'joining_date', 'date'].includes(fieldKey)) {
                                        const formattedTime = parsedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                        return `${formattedDate} ${formattedTime}`;
                                    }
                                    return formattedDate;
                                }
                            }

                            // Check if string contains HTML tags
                            const isHtml = /<[a-z][\s\S]*>/i.test(strVal);

                            if (isHtml || strVal.length > 50) {
                                return (
                                    <div
                                        className="p-2 rounded border shadow-xs"
                                        style={{
                                            maxHeight: '180px',
                                            overflowY: 'auto',
                                            fontSize: '13px',
                                            backgroundColor: '#f8fafc',
                                            wordBreak: 'break-word',
                                            whiteSpace: isHtml ? 'normal' : 'pre-wrap'
                                        }}
                                    >
                                        {isHtml ? (
                                            <div dangerouslySetInnerHTML={{ __html: strVal }} />
                                        ) : (
                                            strVal
                                        )}
                                    </div>
                                );
                            }

                            return strVal;
                        };

                        return (
                            <tr key={key}>
                                <td className="fw-bold">{key}</td>
                                <td className="text-danger">
                                    {formatVal(oldVal, key)}
                                </td>
                                <td className="text-success">
                                    {formatVal(newVal, key)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        );
    };

    const columns = [
        {
            name: "Sl.No",
            selector: (row: any, index: any) => (currentPage - 1) * perPage + index + 1,
            width: "80px",
        },
        {
            name: "Employee",
            selector: (row: AuditLogItem) => row.user?.name || "System",
            sortable: true,
            sortField: "description",
            minWidth: "160px",
            cell: (row: AuditLogItem) => (
                <div className="py-1">
                    <div className="fw-bold">{row.user ? row.user.name : "System Task"}</div>
                    {row.user?.email && <small className="text-muted">{row.user.email}</small>}
                </div>
            )
        },
        {
            name: <div><span className="mx-1">Date & Time</span></div>,
            selector: (row: AuditLogItem) => row.created_at,
            sortable: true,
            sortField: "created_at",
            width: "140px",
            cell: (row: AuditLogItem) => (
                <div className="py-1">
                    <div className="fw-semibold">{new Date(row.created_at).toLocaleDateString()}</div>
                    <small className="text-muted">{new Date(row.created_at).toLocaleTimeString()}</small>
                </div>
            )
        },
        {
            name: "Action",
            selector: (row: AuditLogItem) => row.action,
            sortable: true,
            sortField: "action",
            width: "100px",
            cell: (row: AuditLogItem) => renderActionBadge(row.action)
        },
        {
            name: "Module",
            selector: (row: AuditLogItem) => row.log_name,
            sortable: true,
            sortField: "log_name",
            minWidth: "190px",
            cell: (row: AuditLogItem) => (
                <div className="d-flex flex-column align-items-start gap-1 py-1">
                    <span className="badge bg-light text-dark border text-wrap text-start" style={{ lineHeight: "1.3" }}>
                        {row.log_name ? row.log_name.replace(/_/g, ' ').toUpperCase() : "GENERAL"}
                    </span>
                    {row.subject_id && (
                        <small className="text-primary fw-semibold">
                            (ID #{row.subject_id})
                        </small>
                    )}
                </div>
            )
        },
        {
            name: "Description",
            selector: (row: AuditLogItem) => row.description,
            sortable: true,
            sortField: "description",
            minWidth: "220px",
            grow: 2,
            cell: (row: AuditLogItem) => <span className="py-1">{row.description}</span>,
            wrap: true
        },
        {
            name: "IP Address",
            selector: (row: AuditLogItem) => row.ip_address,
            sortable: true,
            sortField: "ip_address",
            width: "140px",
            cell: (row: AuditLogItem) => (
                <code className="text-secondary">{row.ip_address || "127.0.0.1"}</code>
            )
        },
        {
            name: "Actions",
            width: "110px",
            cell: (row: AuditLogItem) => (
                <Button
                    color="outline-primary"
                    size="sm"
                    className="p-1 px-2"
                    onClick={() => handleViewDetails(row)}
                    title="View Change Diff"
                >
                    <Eye size={14} className="me-1" /> View
                </Button>
            )
        }
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        cells: {
            style: { paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px' },
        },
    };


    return (
        <React.Fragment>
            <Breadcrumbs pageTitle="Audit Logs" parent="User Management" title="Employee Activity Logs" />
            <Container fluid={true} style={{ minHeight: '70vh' }}>


                <Card className="shadow-sm" style={{ minHeight: '65vh' }}>
                    <Row className="m-3 align-items-center">
                        <Col md={12}>
                            <H3 className="text-primary mb-0">Employee Activity Audit Trail</H3>
                        </Col>
                    </Row>
                    <CardBody>
                        <Row className="g-3 mb-4 justify-content-end">
                            <Col lg={3} md={4}>
                                <DatePicker
                                    selected={startDate ? new Date(startDate) : null}
                                    onChange={(date: Date) => setStartDate(date ? formatDate(date) : "")}
                                    placeholderText="Start date"
                                    className="form-control bg-white"
                                    dateFormat="yyyy-MM-dd"
                                    isClearable
                                />
                            </Col>
                            <Col lg={3} md={4}>
                                <DatePicker
                                    selected={endDate ? new Date(endDate) : null}
                                    onChange={(date: Date) => setEndDate(date ? formatDate(date) : "")}
                                    placeholderText="End date"
                                    className="form-control bg-white"
                                    dateFormat="yyyy-MM-dd"
                                    isClearable
                                />
                            </Col>
                            <Col lg={3} md={4}>
                                <InputGroup>
                                    <Input
                                        type="text"
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                        <DataTable
                            columns={columns}
                            data={logs}
                            progressPending={loading}
                            progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationPerPage={perPage}
                            onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
                            onChangePage={(page) => setCurrentPage(page)}
                            sortServer
                            onSort={handleSort}
                            highlightOnHover
                            striped
                            customStyles={customStyles}
                            noDataComponent={
                                <div className="d-flex justify-content-center align-items-center w-100 py-5">
                                    <Card className="shadow-xs border text-center" style={{ maxWidth: "340px", width: "100%", backgroundColor: "#f8fafc" }}>
                                        <CardBody className="p-4">
                                            <div className="rounded-circle bg-primary text-primary mx-auto mb-3 d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px" }}>
                                                <Activity size={24} />
                                            </div>
                                            <h6 className="fw-bold mb-1 text-dark">No Audit Logs Found</h6>
                                            <small className="text-muted">No activity records match your selected filter criteria.</small>
                                        </CardBody>
                                    </Card>
                                </div>
                            }
                        />
                    </CardBody>
                </Card>

                {/* Diff Comparison Modal */}
                <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg" centered>
                    <ModalHeader toggle={() => setViewModal(false)}>
                        Audit Log Details & Field Diff
                    </ModalHeader>
                    {selectedLog && (
                        <ModalBody>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <p className="mb-1"><strong>Employee:</strong> {selectedLog.user ? `${selectedLog.user.name} (${selectedLog.user.email})` : 'System'}</p>
                                    <p className="mb-1"><strong>Action:</strong> {renderActionBadge(selectedLog.action)}</p>
                                    <p className="mb-1"><strong>Module:</strong> {selectedLog.log_name.toUpperCase()}</p>
                                </Col>
                                <Col md={6}>
                                    <p className="mb-1"><strong>Date & Time:</strong> {new Date(selectedLog.created_at).toLocaleString()}</p>
                                    <p className="mb-1"><strong>IP Address:</strong> <code>{selectedLog.ip_address}</code></p>
                                    <p className="mb-1"><strong>Target ID:</strong> #{selectedLog.subject_id || 'N/A'}</p>
                                </Col>
                            </Row>

                            <div className="alert alert-info py-2 mb-3">
                                <strong>Summary:</strong> {selectedLog.description}
                            </div>

                            <h5 className="fw-bold mb-2">Field-Level Changes (Old vs New):</h5>
                            {renderDiffTable(selectedLog.old_values, selectedLog.new_values)}

                            {selectedLog.user_agent && (
                                <div className="mt-3">
                                    <small className="text-muted d-block">User-Agent: {selectedLog.user_agent}</small>
                                </div>
                            )}
                        </ModalBody>
                    )}
                </Modal>
            </Container>
        </React.Fragment>
    );
}
